import { useState, useRef } from "react";
import { Heart } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { EmailAlertDialog } from "@/components/EmailAlertDialog";
import { useFavorites } from "@/context/FavoritesContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const DISMISS_KEY = "str_alert_prompt_dismissed";

export function HeartButton({ product, className }) {
  const [open, setOpen] = useState(false);
  const submitted = useRef(false);
  const { isFavorite, addFavorite, removeFavorite, getAlertEmail } = useFavorites();
  const hearted = isFavorite(product.id);

  const subscribe = async (email) => {
    const { data } = await api.post("/alerts/product", { email, product_id: product.id });
    return data;
  };

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (hearted) {
      removeFavorite(product.id);
      toast("Removed from favorites");
      return;
    }
    addFavorite(product.id);
    const email = getAlertEmail();
    if (email) {
      toast.success("Saved to favorites · alerts on");
      subscribe(email).catch(() => {});
    } else if (sessionStorage.getItem(DISMISS_KEY)) {
      toast.success("Saved to favorites");
    } else {
      toast.success("Saved to favorites");
      setOpen(true);
    }
  };

  const submitEmail = async (email) => {
    try {
      const data = await subscribe(email);
      submitted.current = true;
      toast.success(data.message || "Alerts enabled");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v && !submitted.current) sessionStorage.setItem(DISMISS_KEY, "1");
    submitted.current = false;
  };

  return (
    <>
      <button
        data-testid={`heart-${product.id}`}
        onClick={onClick}
        aria-label={hearted ? "Remove from favorites" : "Save to favorites"}
        className={cn("flex items-center justify-center transition-colors", className)}
      >
        <Heart className={cn("h-4 w-4", hearted ? "fill-danger text-danger" : "")} />
      </button>
      <EmailAlertDialog
        open={open}
        onOpenChange={handleOpenChange}
        title="Get Similar-Item Alerts"
        description={`Saved! Want an email when new ${product.brand ? product.brand + " " : ""}${product.category || "similar"} stock lands? Add your email (optional).`}
        cta="Enable Alerts"
        onSubmit={submitEmail}
      />
    </>
  );
}

